import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaScriptSceneDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

import { PersonaScriptEditableCell } from "./PersonaScriptEditableCell.js";
import { highlightedSceneClass } from "./persona-script-version-highlight.js";

export type PersonaScriptSceneField = "seeing" | "hearing" | "product" | "props";

export interface PersonaScriptSceneTableProps {
  scenes: PersonaScriptSceneDto[];
  isSelected?: boolean;
  canEdit?: boolean;
  editingKey?: string | null;
  aiEditKey?: string | null;
  savingKey?: string | null;
  flashKey?: string | null;
  flashSource?: "manual" | "ai";
  aiBusyKey?: string | null;
  onStartEdit?: (key: string) => void;
  onStopEdit?: () => void;
  onStartAi?: (key: string) => void;
  onStopAi?: () => void;
  onApplyAi?: (key: string, instruction: string) => void;
  onSaveCell?: (sceneNumber: number, field: PersonaScriptSceneField, value: string) => void;
  surface?: "card" | "drawer";
  /** `sceneNumber:field` keys to highlight as changed since the first version. */
  changedCellKeys?: ReadonlySet<string>;
}

export function personaScriptSceneRowClass(index: number, isSelected = false): string {
  if (isSelected && index === 0) return "hover:bg-[var(--row-selected-bg)]";
  if (index === 0) return "bg-ai-fill-light hover:bg-ai-fill-light";
  if (index % 2 === 0) return "bg-muted";
  return "bg-background";
}

const DETAIL_FIELDS = ["hearing", "product", "props"] as const;

/** Same focus ring as the versions-rail controls, so the scroll frame shows keyboard focus. */
export const PERSONA_SCRIPT_SCENE_SCROLL_FOCUS_CLASS =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

export interface PersonaScriptSceneTableLayout {
  indexWidth: string;
  sceneWidth: string;
  /** Width of each of Product and Props. Seeing and Hearing stay flexible. */
  sideWidth: string;
  pad: string;
  headClass: string | undefined;
  cellClass: string | undefined;
  frameClass: string;
  tableClass: string;
}

/** Column widths shared by the live scene table and the drawer's version preview. */
export function personaScriptSceneTableLayout(
  surface: "card" | "drawer",
): PersonaScriptSceneTableLayout {
  if (surface !== "drawer") {
    return {
      indexWidth: "w-12",
      sceneWidth: "w-32",
      sideWidth: "w-40",
      pad: "px-3",
      headClass: undefined,
      cellClass: undefined,
      frameClass: "overflow-x-auto",
      tableClass: "min-w-[1000px]",
    };
  }
  return {
    indexWidth: "w-10",
    sceneWidth: "w-24",
    sideWidth: "w-40",
    pad: "px-2",
    headClass: "text-caption font-medium tracking-label uppercase text-muted-foreground",
    cellClass: "text-drawer-body",
    // Fits beside the open 320px versions rail in the 1200px sheet (~821px frame).
    // Narrower sheets still scroll this frame instead of stacking the columns.
    frameClass: "min-w-0 overflow-x-auto",
    tableClass: "w-full min-w-[760px] table-fixed",
  };
}

function cellKey(sceneNumber: number, field: PersonaScriptSceneField): string {
  return `${sceneNumber}:${field}`;
}

export function parsePersonaScriptSceneCellKey(
  key: string,
): { sceneNumber: number; field: PersonaScriptSceneField } | null {
  const [sceneNumberRaw, field] = key.split(":");
  if (
    field !== "seeing" &&
    field !== "hearing" &&
    field !== "product" &&
    field !== "props"
  ) {
    return null;
  }
  const sceneNumber = Number(sceneNumberRaw);
  if (!Number.isInteger(sceneNumber) || sceneNumber < 0) return null;
  return { sceneNumber, field };
}

export function PersonaScriptSceneTable({
  scenes,
  isSelected = false,
  canEdit = false,
  editingKey = null,
  aiEditKey = null,
  savingKey = null,
  flashKey = null,
  flashSource = "manual",
  aiBusyKey = null,
  onStartEdit,
  onStopEdit,
  onStartAi,
  onStopAi,
  onApplyAi,
  onSaveCell,
  surface = "card",
  changedCellKeys,
}: PersonaScriptSceneTableProps) {
  const { t } = useTranslation();
  const drawer = surface === "drawer";
  const { indexWidth, sceneWidth, sideWidth, pad, headClass, cellClass, frameClass, tableClass } =
    personaScriptSceneTableLayout(surface);
  const fieldLabel: Record<PersonaScriptSceneField, string> = {
    seeing: t("personaOpportunities.scripts.seeing", "Seeing"),
    hearing: t("personaOpportunities.scripts.hearing", "Hearing"),
    product: t("personaOpportunities.scripts.product", "Product"),
    props: t("personaOpportunities.scripts.props", "Props"),
  };
  const cellTextClass = cn(pad, "py-3 align-top leading-relaxed", drawer && "break-words", cellClass);

  function isChanged(scene: PersonaScriptSceneDto, field: PersonaScriptSceneField): boolean {
    return changedCellKeys?.has(cellKey(scene.sceneNumber, field)) ?? false;
  }

  function renderField(scene: PersonaScriptSceneDto, field: PersonaScriptSceneField) {
    const key = cellKey(scene.sceneNumber, field);
    return (
      <PersonaScriptEditableCell
        value={scene[field] ?? ""}
        canEdit={canEdit}
        isEditing={editingKey === key}
        isAiOpen={aiEditKey === key}
        isAiBusy={aiBusyKey === key}
        isFlashing={flashKey === key && flashSource !== "ai"}
        isFlashingAi={flashKey === key && flashSource === "ai"}
        isSaving={savingKey === key}
        onStartEdit={() => onStartEdit?.(key)}
        onStopEdit={onStopEdit}
        onStartAi={() => onStartAi?.(key)}
        onStopAi={onStopAi}
        onApplyAi={(instruction) => onApplyAi?.(key, instruction)}
        onSave={(next) => onSaveCell?.(scene.sceneNumber, field, next)}
        surface={surface}
      />
    );
  }

  return (
    <div
      className={cn(
        "rounded-sm border border-border",
        frameClass,
        drawer && PERSONA_SCRIPT_SCENE_SCROLL_FOCUS_CLASS,
      )}
      tabIndex={drawer ? 0 : undefined}
      role={drawer ? "region" : undefined}
      aria-label={
        drawer ? t("personaOpportunities.scripts.sceneColumnsScroll", "Scene columns") : undefined
      }
    >
      <Table className={tableClass}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={cn(indexWidth, pad, headClass)}>
              {t("personaOpportunities.scripts.sceneNumberColumn", "#")}
            </TableHead>
            <TableHead className={cn(sceneWidth, pad, headClass)}>
              {t("personaOpportunities.scripts.sceneColumn", "Scene")}
            </TableHead>
            <TableHead className={cn(pad, headClass)}>{fieldLabel.seeing}</TableHead>
            <TableHead className={cn(pad, headClass)}>{fieldLabel.hearing}</TableHead>
            <TableHead className={cn(sideWidth, pad, headClass)}>{fieldLabel.product}</TableHead>
            <TableHead className={cn(sideWidth, pad, headClass)}>{fieldLabel.props}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scenes.map((scene, index) => (
            <TableRow
              key={scene.id}
              data-state={isSelected && index === 0 ? "selected" : undefined}
              className={personaScriptSceneRowClass(index, isSelected)}
            >
              <TableCell className={cn(pad, "py-3 align-top tabular-nums text-muted-foreground", cellClass)}>
                {scene.sceneNumber}
              </TableCell>
              <TableCell className={cn(pad, "py-3 align-top", drawer && "break-words")}>
                <span
                  className={
                    drawer
                      ? "inline-block max-w-full break-words rounded-sm border border-border bg-background px-2 py-0.5 text-caption text-foreground"
                      : "inline-block rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] text-foreground"
                  }
                >
                  {scene.sceneName}
                </span>
              </TableCell>
              {(["seeing", ...DETAIL_FIELDS] as const).map((field) => (
                <TableCell
                  key={field}
                  className={cn(cellTextClass, isChanged(scene, field) && highlightedSceneClass(true))}
                >
                  {renderField(scene, field)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
